/* eslint-disable @typescript-eslint/ban-types */
import { User } from '@models/user.model';
import { TestCasePriority } from '@modules/test-development/models/test-case-priority.model';
import {
  Base,
  PageObject,
  TestCaseResult,
  TestCaseDataDrivenCondition,
} from '@testsigmainc/models';
import { TestCaseStatus, PreRequisiteRunType } from '@testsigmainc/constants';
import { immerable } from 'immer';
import {
  alias,
  custom,
  deserialize,
  getDefaultModelSchema,
  object,
  optional,
  serializable,
  serialize,
  SKIP,
} from 'serializr';
import { AfterTestCaseConfig } from './after-test-case-config';
import { Requirement } from './requirement.model';
import { EntityUpdateType } from '../enums/entity-update-type.enum';
import { EntityCreateType } from '../enums/entity-create-type.enum';

export class TestCase extends Base implements PageObject {
  // self reference is not supported in babel 6.X
  // reference: https://www.npmjs.com/package/serializr
  [immerable] = true;

  constructor() {
    super();
    getDefaultModelSchema(TestCase).props['preRequisiteCase'] = optional(
      object(TestCase),
    );
    getDefaultModelSchema(TestCase).props['afterTestCase'] = optional(
      object(TestCase),
    );
  }
  @serializable
  public id: number;

  @serializable
  public name: string;

  @serializable
  public comments: string;

  @serializable
  public description: string;

  @serializable(
    custom(
      (v) => v,
      (v) => {
        if (v && typeof v == 'string') return JSON.parse(v);
        else if (v) return v;
      },
    ),
  )
  public customFields: Object;

  @serializable
  public status: TestCaseStatus;

  @serializable
  public isManual: boolean;

  @serializable
  sendMailNotification = false;

  @serializable
  public isStepGroup: boolean;

  @serializable
  public assignee: number;

  @serializable
  public readyBy: number;

  @serializable
  public requirementId: number;

  @serializable(optional(object(Requirement)))
  public requirement: Requirement;

  @serializable(alias('priority'))
  public priorityId: number;

  @serializable
  public type: number;

  @serializable
  public baseUrl: string;

  @serializable
  public baseAppId: number;

  @serializable
  public baseAppVersionId: number;

  @serializable
  public preRequisite: number;

  @serializable
  public preRequisiteRunType: PreRequisiteRunType;

  @serializable
  public isDataDriven: boolean;

  @serializable(optional(object(TestCaseDataDrivenCondition)))
  public testCaseDataDrivenCondition: TestCaseDataDrivenCondition;

  @serializable(optional(object(User)))
  public assignedTo: User;

  @serializable
  public applicationVersionId: number;

  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return new TestCaseResult().deserialize(v);
      },
    ),
  )
  public lastRun: TestCaseResult;

  @serializable
  public createdById: number;

  @serializable(alias('reviewedBy'))
  public reviewedById: number;

  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return new Date(v);
      },
    ),
  )
  public reviewedAt: Date | undefined;

  @serializable
  public updatedById: number;

  @serializable(alias('obsoleteBy'))
  public obsoleteById: number;

  @serializable(alias('reviewSubmittedBy'))
  public reviewSubmittedById: number;

  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return new Date(v);
      },
    ),
  )
  public reviewSubmittedAt: Date | undefined;

  @serializable
  public deleted: boolean;

  @serializable(optional())
  public preRequisiteCase: TestCase;

  @serializable(optional(object(User)))
  public reviewedByUser: User;

  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public tags: string[];

  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public replaceTags: string[];

  @serializable
  public hasAfterTest = false;

  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public afterTestParentId: number;

  @serializable(optional(object(AfterTestCaseConfig)))
  public afterTestCaseConfig: AfterTestCaseConfig;

  @serializable(optional())
  public afterTestCase: TestCase;

  @serializable(optional(object(TestCasePriority)))
  public testCasePriority: TestCasePriority;

  @serializable
  public considerVisualTestResult: boolean;

  @serializable
  public isEligibleForAfterSuite = false;

  @serializable
  public comment: string;

  @serializable
  public hasError: boolean;

  @serializable(optional())
  public aiDescription: string;

  @serializable(optional())
  public createType: EntityCreateType;

  @serializable(optional())
  public updateType: EntityUpdateType;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(TestCase, input));
  }

  public startUrl: string;

  public parentCase: TestCase;

  @serializable
  public scenarioId: number;

  public scenario: any;

  public feature: any;

  public serialize(): JSON {
    return serialize(this);
  }

  public get isAIGenerated(): boolean {
    const aiUpdateTypes = [EntityUpdateType.AI, EntityUpdateType.AI_AGENT];
    const aiCreateTypes = [EntityCreateType.AI, EntityCreateType.AI_AGENT];

    if (this.updateType) {
      return aiUpdateTypes.includes(this.updateType);
    }

    return aiCreateTypes.includes(this.createType);
  }
}
